import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ConfirmationService, MessageService } from 'primeng/api';
import {HttpBackend, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import { HttpInterceptorsProvider } from '@/common/http/interceptors.provider';
import { provideRouter, Router, RouteReuseStrategy, withNavigationErrorHandler } from '@angular/router';
import { routes } from './app.routes';
import { provideStaleChunkReloadReset, reloadOnStaleChunk } from '@/common/router/reload-on-stale-chunk';
import { NewComponentPerIdStrategy } from '@/common/router/new-component-per-id';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import {providePrimeNG} from 'primeng/config';
import {CrowdSync} from "@/crowdsync.theme";
import * as Sentry from '@sentry/angular';
import { environment } from '@env/environment';

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
	// The release (the deploy's commit) in each URL: a browser's copy from before a deploy would show new text as keys
	const suffix = `.json?v=${encodeURIComponent(environment.releaseSHA)}`;

	return new MultiTranslateHttpLoader(_httpBackend, [
		'/assets/i18n/generic/',
		'/assets/i18n/login/',
		'/assets/i18n/profile/',
		'/assets/i18n/person-mapping/',
		'/assets/i18n/project/',
		'/assets/i18n/settings/',
		'/assets/i18n/sync/',
		'/assets/i18n/user/',
		'/assets/i18n/dev-tools/',
	].map(prefix => ({prefix, suffix})));
}

export const provideTranslation = () => ({
	defaultLanguage: 'en',
	useDefaultLang: true,
	loader: {
		provide: TranslateLoader,
		useFactory: HttpLoaderFactory,
		deps: [
			HttpBackend,
		],
	},
});

export const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(routes, withNavigationErrorHandler(reloadOnStaleChunk)),
		provideStaleChunkReloadReset(),
		// A link to another record on the same route (a project's page, its health view) loads that record (task 4.9)
		{ provide: RouteReuseStrategy, useClass: NewComponentPerIdStrategy },
		provideAnimationsAsync(),
		providePrimeNG({
			ripple: true,
			theme: {
				preset: CrowdSync,
				options: {
					darkModeSelector: false,
					// Same order as declared at the top of src/tailwind.css, so Tailwind
					// utilities override PrimeNG component styles.
					cssLayer: {
						name: 'primeng',
						order: 'theme, base, primeng, components, utilities'
					}
				}
			}
		}),
		provideHttpClient(withInterceptorsFromDi()),
		importProvidersFrom([
			TranslateModule.forRoot(provideTranslation()),
		]),
		HttpInterceptorsProvider,
		ConfirmationService,
		MessageService,
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler(),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
	],
};
